import { _decorator, Node, Component, Button, randomRangeInt } from 'cc';
import Lobby from '../../../System/Lobby';
import FullscreenPage from '../../UI/Popup/FullscreenPage';
import MachinePortal from '../../UI/GameObject/MachinePortal';
import enterTableFlow from '../EnterTableFlow';
import Page from '../../../../common/scripts/Toolkit/Components/Page/Page';
import ValueToggleSelector from '../../../../common/scripts/Toolkit/Components/ValuePicker/ValueToggleSelector';
import ValueSelector from '../../../../common/scripts/Toolkit/Helper/ValueSelector';
import ItemGenerator from '../../../../common/scripts/Toolkit/Components/Misc/ItemGenerator';
import { Unit } from '../../../../common/scripts/Account';
import { FeatureActivityDataType } from '../../Data/Config/FeatureActivityConfig';
import { EnterTableOption, GamePreviewData } from '../../../../tableEngine/Script/Game/Data/Model/HallModel';
import { PlayType } from '../../../../tableEngine/Script/Game/Data/Model/TableModel';
import { LobbyGamePreviewData } from '../../Data/Model/LobbyHallModel';
import InfiniteScrollView from '../../../../common/scripts/Toolkit/Components/ScrollView/InfiniteScrollView';
import Dropdown from '../../../../common/scripts/Toolkit/Components/Dropdown/Dropdown';
import LobbyMultiplePlayGamePreviewData from '../../Data/DataObject/PreviewData/LobbyMultiplePlayGamePreviewData';
import { GameTagOption } from '../../UI/Popup/GameTagFilterDropdown';
import LobbyRoundPlayGamePreviewData from '../../Data/DataObject/PreviewData/LobbyRoundPlayGamePreviewData';
import { SceneName } from '../../Data/Config/SceneNodeName';

const { ccclass, property } = _decorator;

@ccclass('OuterLobbyUIController')
export default class OuterLobbyUIController extends Component {
    @property(Node)
    private readonly lobbyView: Node = null;

    @property(ValueToggleSelector)
    private readonly tabSelector: ValueToggleSelector = null;

    @property(InfiniteScrollView)
    private readonly machinePortalList: InfiniteScrollView = null;

    @property(ItemGenerator)
    private readonly basicMachinePortalGenerator: ItemGenerator = null;

    @property(Node)
    private readonly autoEnterMask: Node = null;

    @property(Dropdown)
    private readonly gameTagDropdown: Dropdown = null;

    @property(Button)
    private readonly quickJoinButton: Button = null;

    private _enteringGame = false;
    private _tryingQuickJoinGame = false;
    private _tabValueSelector: ValueSelector<number> = null;
    private _hasRegisteredEvent = false;
    private _gameDatas: GamePreviewData[] = [];
    private _favoriteGameDatas: GamePreviewData[] = [];
    // private _enterMachineDialog: EnterMachineDialog = null;

    // private get enterMachineDialog() {
    //     if (!this._enterMachineDialog) {
    //         this._enterMachineDialog = Lobby.scene.getNode(NodeName.EnterMachineDialog).getComponent(EnterMachineDialog);
    //     }
    //     return this._enterMachineDialog;
    // }

    private get isShowingFavoriteGame() {
        return this.gameTagDropdown.selectedOptionValue === GameTagOption.Favorite;
    }

    public async onLoad() {
        this._registerEvent();
    }

    public onEnable() {
        Lobby.account.unit = Unit.Dollar;
        Lobby.account.denom = Lobby.hall.denom;
        this._onSelectedTabChanged();
        Page.EVENT_TARGET.on(Page.EVENT_TYPE.VISIBLE_PAGE_COUNT_CHANGED, this._onVisiblePageCountChanged, this);
        FullscreenPage.EVENT_TARGET.on(FullscreenPage.EVENT_INSTANCE_ADDED, this._onVisiblePageCountChanged, this);
        this.gameTagDropdown.on(Dropdown.EVENT_TYPE.SELECTED_OPTION_CHANGED, this._updateDropDownOption, this);
    }

    public onDisable() {
        Page.EVENT_TARGET.off(Page.EVENT_TYPE.VISIBLE_PAGE_COUNT_CHANGED, this._onVisiblePageCountChanged, this);
        FullscreenPage.EVENT_TARGET.off(FullscreenPage.EVENT_INSTANCE_ADDED, this._onVisiblePageCountChanged, this);
        this.gameTagDropdown.off(Dropdown.EVENT_TYPE.SELECTED_OPTION_CHANGED, this._updateDropDownOption, this);
    }

    public async joinTable(enterTableOption: Partial<EnterTableOption> = {}) {
        if (this._enteringGame) {
            return;
        }
        const resumeTable = enterTableFlow.findTableCanBeResumed();
        // if there is a table that can be resumed, enter the lobby to show resume game popup
        if (resumeTable) {
            Lobby.scene.enter(SceneName.Lobby);
            // enter to trigger resume game popup
            enterTableFlow.enter(enterTableOption, FeatureActivityDataType.EnterGameLocation.OuterLobby);
        } else {
            await enterTableFlow.enter(enterTableOption, FeatureActivityDataType.EnterGameLocation.OuterLobby);
        }
    }

    public forceUpdateGameList() {
        this._updateMachinePortalList();
    }

    private _registerEvent() {
        if (this._hasRegisteredEvent) {
            return;
        }
        this._hasRegisteredEvent = true;
        // handle tab selector
        this.autoEnterMask.active = false;
        this._tabValueSelector = new ValueSelector(Lobby.config.supportedPlayType.map((playType) => +PlayType.map(playType)));
        this.tabSelector.init(
            this._tabValueSelector,
            () => {},
            (option: Node, isSelected: boolean) => {
                option.getComponent(Button).keep = isSelected;
            },
        );
        this._tabValueSelector.on(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this._onSelectedTabChanged, this);

        // event listeners
        this.machinePortalList.node.on(InfiniteScrollView.EVENT_TYPE.FIRST_ITEM_INDEX_CHANGED, this._updateMachinePortalList, this);
        this.basicMachinePortalGenerator.on(ItemGenerator.EVENT_TYPE.ITEM_GENERATED, this._onMachinePortalGenerated, this);
        this.quickJoinButton.node.on(Button.EventType.CLICK, this._quickJoinGame, this);
    }

    private _updateMachinePortalList() {
        this.basicMachinePortalGenerator.items.forEach((machineItem, index) => {
            const idx = this.machinePortalList.firstItemIndex + index;
            const gameDataSource = this.isShowingFavoriteGame ? this._favoriteGameDatas : this._gameDatas;
            if (idx >= gameDataSource.length) {
                return;
            }
            const gameData = gameDataSource[idx];
            const machinePortal = machineItem.getComponent(MachinePortal);
            machinePortal.display(gameData as LobbyGamePreviewData);
        });
    }

    private async _onSelectedTabChanged() {
        const currentGameType = PlayType.map(this._tabValueSelector.value);
        Lobby.hall.updatePlayType(currentGameType);
        Lobby.hall.requestOverviewData();
        this.machinePortalList.scrollView.stopAutoScroll();
        this.machinePortalList.scrollView.scrollToTop();
        this.machinePortalList.node.active = false;
        await Lobby.hall.waitForHallOverviewData();
        this.machinePortalList.node.active = true;
        this._gameDatas = Lobby.hall.gameList.filter((gameData) => gameData.GameType === Lobby.hall.currentPlayType);
        this._updateDropDownOption();
    }

    private _updateDropDownOption() {
        if (this.isShowingFavoriteGame) {
            this._favoriteGameDatas = this._gameDatas.filter(
                (gameData: LobbyMultiplePlayGamePreviewData | LobbyRoundPlayGamePreviewData) => gameData.IsFavorite,
            );
            this.machinePortalList.itemCount = this._favoriteGameDatas.length;
        } else {
            this.machinePortalList.itemCount = this._gameDatas.length;
        }
        this.machinePortalList.scrollView.stopAutoScroll();
        this.machinePortalList.scrollView.scrollToTop();
        this._updateMachinePortalList();
    }

    private _onMachinePortalGenerated(item: Node) {
        item.on(Node.EventType.TOUCH_END, async () => {
            const gameType = Lobby.hall.currentPlayType;
            const { GameID: gameID } = item.getComponent(MachinePortal).displayData;
            const gameUrl = await Lobby.hall.getGameUrl(gameID, gameType);
            Lobby.sharedAsset.webViewManager.openGame(gameUrl, gameID, gameType);
            // await this._enterGame(hallID, gameID);
        });
    }

    // private async _enterGame(hallID: number, gameID: string) {
    //     if (this._enteringGame) {
    //         return;
    //     }
    //     this._enteringGame = true;
    //     await Lobby.hall.requestTableListOfGame(hallID, gameID);
    //     Lobby.scene.enter(SceneName.Lobby);
    //     this._enteringGame = false;
    // }

    private _onVisiblePageCountChanged() {
        // hide lobby view when there is a fullscreen page visible
        this.lobbyView.active = !Page.INSTANCES.find((page) => page.isVisible && FullscreenPage.INSTANCES.includes(page));
    }

    private async _quickJoinGame() {
        if (this._tryingQuickJoinGame) {
            return;
        }
        this._tryingQuickJoinGame = true;

        try {
            const gameDatas = [...this._gameDatas];
            for (let i = gameDatas.length - 1; i > 0; i--) {
                const randomIndex = randomRangeInt(0, i + 1);
                [gameDatas[i], gameDatas[randomIndex]] = [gameDatas[randomIndex], gameDatas[i]];
            }

            for (const { GameID: gameID, GameType: gameType } of gameDatas) {
                const gameUrl = await Lobby.hall.getGameUrl(gameID, gameType);
                if (gameUrl) {
                    Lobby.sharedAsset.webViewManager.openGame(gameUrl, gameID, gameType);
                    break;
                }
            }
        } finally {
            this._tryingQuickJoinGame = false;
        }
    }
}
